"use client";

import * as React from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  AlertTriangle,
  Check,
  Clock,
  DollarSign,
  Search,
  TextSearch,
  Trash,
} from "lucide-react";
import type { IGetSalonServicesByOwnerIdAction } from "@/actions/owner/types";
import { format, parse } from "date-fns";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import hooks from "@/hooks";
import Modal from "@/components/core/Modal";
import { FaRegEdit } from "react-icons/fa";
import { TimePicker } from "@/components/ui/datetime-picker";
import { cn } from "@/lib/utils";
import { UserJWTDecoded } from "@/types";

interface IOwnerCompanyServicesTable {
  data: IGetSalonServicesByOwnerIdAction["Response"];
  user: UserJWTDecoded["user"];
}

const OwnerCompanyServicesTable = ({
  data,
  user,
}: IOwnerCompanyServicesTable) => {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const [servicesData, setServicesData] = React.useState(data);
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [selectedService, setSelectedService] = React.useState<
    IGetSalonServicesByOwnerIdAction["Response"][number] | null
  >(null);
  const { toast } = useToast();

  const updateService = hooks.useEditService({
    onSuccess: (data) => {
      toast({
        title: "Serviço atualizado.",
        description: "Serviço atualizado com sucesso.",
        variant: "success",
      });
    },
    onError: (err) => {
      console.log("Error", err);
      toast({
        title: "Falha ao atualizar o serviço",
        description: "Não foi possível atualizar o serviço",
        variant: "warning",
      });
    },
  });

  React.useEffect(() => {
    setServicesData(data);
  }, [data]);

  const [serviceName, setServiceName] = React.useState("");
  const [serviceValue, setServiceValue] = React.useState("");
  const [serviceDuration, setServiceDuration] = React.useState("");
  const [observation, setObservation] = React.useState("");

  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [activeModalOpen, setActiveModalOpen] = React.useState(false);
  const [serviceToDelete, setServiceToDelete] = React.useState<
    IGetSalonServicesByOwnerIdAction["Response"][number] | null
  >(null);
  const [serviceToActive, setServiceToActive] = React.useState<
    IGetSalonServicesByOwnerIdAction["Response"][number] | null
  >(null);
  const [confirmServiceName, setConfirmServiceName] = React.useState("");

  const handleOpenActiveModal = (
    service: IGetSalonServicesByOwnerIdAction["Response"][number],
  ) => {
    setServiceToActive(service);
    setActiveModalOpen(true);
  };

  const handleOpenDeleteModal = (
    service: IGetSalonServicesByOwnerIdAction["Response"][number],
  ) => {
    setServiceToDelete(service);
    setDeleteModalOpen(true);
  };

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, "");

    const numberValue = Number(numericValue) / 100;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numberValue);
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    if (rawValue === "") {
      setServiceValue("");
      return;
    }

    const formattedValue = formatCurrency(rawValue);
    setServiceValue(formattedValue);
  };

  const handleOpenEditModal = (
    service: IGetSalonServicesByOwnerIdAction["Response"][number],
  ) => {
    setSelectedService(service);
    setEditModalOpen(true);
  };

  React.useEffect(() => {
    if (selectedService) {
      setServiceName(selectedService.name);

      const numericValue =
        typeof selectedService.value === "string"
          ? Number.parseFloat(selectedService.value)
          : selectedService.value;

      setServiceValue(
        new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(numericValue),
      );

      setServiceDuration(
        format(parse(selectedService.duration, "HHmm", new Date()), "HH:mm"),
      );

      setObservation(
        selectedService.observation ? selectedService.observation : "",
      );
    }
  }, [selectedService]);

  const handleSave = async () => {
    if (!selectedService) return;

    if (!serviceName.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, insira um nome para o serviço.",
        variant: "warning",
      });
      return;
    }

    if (!serviceValue) {
      toast({
        title: "Valor obrigatório",
        description: "Por favor, insira um valor para o serviço.",
        variant: "warning",
      });
      return;
    }

    if (!serviceDuration) {
      toast({
        title: "Duração obrigatória",
        description: "Por favor, insira uma duração para o serviço.",
        variant: "warning",
      });
      return;
    }

    if (observation && observation.length > 255) {
      toast({
        title: "Observação muito longa",
        description: "A observação deve ter no máximo 255 caracteres.",
        variant: "warning",
      });
      return;
    }

    try {
      const numericValue = Number(serviceValue.replace(/\D/g, "")) / 100;

      const durationWithoutColon = serviceDuration.replace(":", "");

      await updateService.mutateAsync({
        vocationId: selectedService.id,
        name: serviceName,
        value: numericValue,
        duration: durationWithoutColon,
        observation: observation ?? "",
        salonId: selectedService.salonId,
        deleted_at: undefined,
        updated_at: undefined,
      });

      setServicesData((prev) =>
        prev.map((service) =>
          service.id === selectedService.id
            ? {
                ...service,
                name: serviceName,
                value: numericValue.toString(),
                duration: durationWithoutColon,
                observation: observation ?? "",
              }
            : service,
        ),
      );

      setEditModalOpen(false);

      toast({
        title: "Serviço atualizado",
        description: "O serviço foi atualizado com sucesso.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Falha ao atualizar",
        description: "Não foi possível atualizar o serviço.",
        variant: "warning",
      });
    }
  };

  const activeService = hooks.useActiveService({
    onSuccess: () => {
      toast({
        title: "Serviço ativado com sucesso!",
        variant: "success",
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Não foi possível ativar o serviço",
        description: (error as Error).message,
        variant: "warning",
      });
    },
  });

  const deleteService = hooks.useRemoveService({
    onSuccess: () => {
      toast({
        title: "Serviço excluído.",
        description: "O serviço foi removido com sucesso.",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Falha ao excluir",
        description: "Não foi possível excluir o serviço.",
        variant: "warning",
      });
    },
  });

  const handleConfirmActive = async () => {
    if (!serviceToActive) return;

    try {
      await activeService.mutateAsync(serviceToActive.id);

      setServicesData((prev) =>
        prev.map((service) =>
          service.id === serviceToActive.id
            ? {
                ...service,
                deleted_at: undefined,
              }
            : service,
        ),
      );

      setActiveModalOpen(false);
      setConfirmServiceName("");

      toast({
        title: "Serviço ativado !",
        description: "O serviço foi ativado com sucesso.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Falha ao a tivar o serviço",
        description: "Não foi possível ativar o serviço.",
        variant: "warning",
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!serviceToDelete) return;

    try {
      await deleteService.mutateAsync(serviceToDelete.id);

      setServicesData((prev) =>
        prev.map((service) =>
          service.id === serviceToDelete.id
            ? {
                ...service,
                deleted_at: new Date().toISOString(),
              }
            : service,
        ),
      );

      setDeleteModalOpen(false);
      setConfirmServiceName("");

      toast({
        title: "Serviço excluído",
        description: "O serviço e foi removido com sucesso.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Falha ao excluir o serviço",
        description: "Não foi possível excluir o serviço.",
        variant: "warning",
      });
    }
  };

  const columns: ColumnDef<
    IGetSalonServicesByOwnerIdAction["Response"][number]
  >[] = [
    {
      accessorKey: "name",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 hover:bg-transparent"
          >
            Nome
            <div className="flex flex-col ml-1">
              {column.getIsSorted() === "asc" ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  style={{ width: "12px", height: "13px" }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <path d="m5 15 7-7 7 7" />
                </svg>
              ) : column.getIsSorted() === "desc" ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  style={{ width: "12px", height: "13px" }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <path d="m19 9-7 7-7-7" />
                </svg>
              ) : (
                <div className="flex flex-col opacity-40">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    style={{ width: "12px", height: "13px" }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="-mb-1"
                  >
                    <path d="m5 15 7-7 7 7" />
                  </svg>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    style={{ width: "12px", height: "13px" }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="-mt-1"
                  >
                    <path d="m19 9-7 7-7-7" />
                  </svg>
                </div>
              )}
            </div>
          </Button>
        );
      },
      cell: ({ row }) => row.getValue("name"),
      filterFn: "includesString",
    },
    {
      accessorKey: "value",
      header: ({ column }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 hover:bg-transparent"
          >
            <DollarSign className="w-4 h-4" />
            Valor
            <div className="flex flex-col ml-1">
              {column.getIsSorted() === "asc" ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  style={{ width: "12px", height: "13px" }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <path d="m5 15 7-7 7 7" />
                </svg>
              ) : column.getIsSorted() === "desc" ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  style={{ width: "12px", height: "13px" }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <path d="m19 9-7 7-7-7" />
                </svg>
              ) : (
                <div className="flex flex-col opacity-40">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    style={{ width: "12px", height: "13px" }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="-mb-1"
                  >
                    <path d="m5 15 7-7 7 7" />
                  </svg>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    style={{ width: "12px", height: "13px" }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="-mt-1"
                  >
                    <path d="m19 9-7 7-7-7" />
                  </svg>
                </div>
              )}
            </div>
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        return (
          <div>
            <span>
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(row.getValue("value"))}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "duration",
      header: ({ column }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 hover:bg-transparent"
          >
            <Clock className="w-4 h-4" />
            Duração média
            <div className="flex flex-col ml-1">
              {column.getIsSorted() === "asc" ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  style={{ width: "12px", height: "13px" }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <path d="m5 15 7-7 7 7" />
                </svg>
              ) : column.getIsSorted() === "desc" ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  style={{ width: "12px", height: "13px" }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <path d="m19 9-7 7-7-7" />
                </svg>
              ) : (
                <div className="flex flex-col opacity-40">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    style={{ width: "12px", height: "13px" }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="-mb-1"
                  >
                    <path d="m5 15 7-7 7 7" />
                  </svg>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    style={{ width: "12px", height: "13px" }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="-mt-1"
                  >
                    <path d="m19 9-7 7-7-7" />
                  </svg>
                </div>
              )}
            </div>
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        return (
          <div>
            <span>
              {format(
                parse(row.getValue("duration"), "HHmm", new Date()),
                "HH:mm",
              )}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "observation",
      header: ({ column }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 hover:bg-transparent"
          >
            <TextSearch className="w-4 h-4" />
            Observação
            <div className="flex flex-col ml-1">
              {column.getIsSorted() === "asc" ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  style={{ width: "12px", height: "13px" }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <path d="m5 15 7-7 7 7" />
                </svg>
              ) : column.getIsSorted() === "desc" ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  style={{ width: "12px", height: "13px" }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <path d="m19 9-7 7-7-7" />
                </svg>
              ) : (
                <div className="flex flex-col opacity-40">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    style={{ width: "12px", height: "13px" }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="-mb-1"
                  >
                    <path d="m5 15 7-7 7 7" />
                  </svg>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    style={{ width: "12px", height: "13px" }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="-mt-1"
                  >
                    <path d="m19 9-7 7-7-7" />
                  </svg>
                </div>
              )}
            </div>
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        return (
          <div>
            <span>
              {String(row.getValue("observation")).length > 50
                ? `${String(row.getValue("observation")).slice(0, 50)}...`
                : row.getValue("observation")}
            </span>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center justify-center w-full gap-2 p-0 hover:bg-transparent"
          >
            Ação
          </Button>
        );
      },
      cell: ({ row }) => {
        const service = row.original;

        return (
          <div className="flex items-center justify-center gap-5">
            <div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleOpenEditModal(service)}
                aria-label="Editar"
                className="hover:bg-transparent min-w-[70px]"
              >
                <FaRegEdit className="h-4 w-4" />
                Editar
              </Button>
            </div>

            {row.original.deleted_at ? (
              <div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenActiveModal(service)}
                  aria-label="Excluir"
                  className="hover:bg-transparent min-w-[70px]"
                >
                  <Check className="h-4 w-4" />
                  Ativar
                </Button>
              </div>
            ) : (
              <div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenDeleteModal(service)}
                  aria-label="Excluir"
                  className="hover:bg-transparent min-w-[70px]"
                >
                  <Trash className="h-4 w-4" />
                  Excluir
                </Button>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: servicesData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: {
      columnFilters,
      globalFilter,
      sorting,
    },
    initialState: {
      pagination: {
        pageSize: 5,
      },
    },
  });

  return (
    <>
      <div className="w-full p-4 sm:p-6 space-y-4 sm:space-y-6 bg-white rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar serviços..."
              value={globalFilter ?? ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-8"
              style={{ borderRadius: "999px" }}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Itens por página:
            </span>
            <Select
              value={table.getState().pagination.pageSize.toString()}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-16">
                <SelectValue
                  placeholder={table.getState().pagination.pageSize}
                />
              </SelectTrigger>
              <SelectContent>
                {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={pageSize.toString()}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1 overflow-x-auto">
          <div>
            <table className="w-full md:min-w-[600px]">
              <thead>
                <tr>
                  {table.getFlatHeaders().map((header) => (
                    <th
                      key={header.id}
                      className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer select-none"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={cn(
                        `${row.index % 2 === 1 ? "bg-muted/50" : "bg-white"}`,
                        "border-b border-gray-100 hover:bg-gray-50 transition-colors",
                        row.original.deleted_at &&
                          "line-through text-gray-400 opacity-70",
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="py-3 px-4">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="h-24 text-center text-muted-foreground"
                    >
                      Nenhum resultado encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            Mostrando{" "}
            <strong>
              {table.getFilteredRowModel().rows.length == 0
                ? 0
                : table.getState().pagination.pageIndex *
                    table.getState().pagination.pageSize +
                  1}
            </strong>{" "}
            a{" "}
            <strong>
              {Math.min(
                (table.getState().pagination.pageIndex + 1) *
                  table.getState().pagination.pageSize,
                table.getFilteredRowModel().rows.length,
              )}
            </strong>{" "}
            de <strong>{table.getFilteredRowModel().rows.length}</strong>{" "}
            resultados
          </div>
          <div className="flex items-center justify-center sm:justify-end space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Anterior
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: table.getPageCount() }, (_, i) => (
                <Button
                  key={i}
                  variant={
                    i === table.getState().pagination.pageIndex
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  className="w-8 h-8"
                  onClick={() => table.setPageIndex(i)}
                >
                  {i + 1}
                </Button>
              )).slice(
                Math.max(0, table.getState().pagination.pageIndex - 1),
                Math.min(
                  table.getPageCount(),
                  table.getState().pagination.pageIndex + 4,
                ),
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Próximo
            </Button>
          </div>
        </div>
      </div>

      {selectedService && (
        <Modal
          isOpen={editModalOpen}
          onOpenChange={setEditModalOpen}
          dialogTitle="Editar serviço"
          dialogDescription={`Editar detalhes do serviço ${selectedService.name}`}
          className="flex flex-col gap-6 p-6 sm:p-8 max-w-md"
        >
          <div className="w-full flex-1">
            <div className="mb-6">
              <span className="font-semibold text-base">Editar Serviço</span>
            </div>

            <div className="space-y-4">
              <div className="grid gap-2">
                <label htmlFor="name" className="text-sm font-semibold">
                  Nome
                </label>
                <Input
                  id="name"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  placeholder="Nome do serviço"
                />
              </div>

              <div className="grid gap-2">
                <label htmlFor="value" className="text-sm font-semibold">
                  Valor
                </label>
                <Input
                  id="value"
                  value={serviceValue}
                  onChange={handleValueChange}
                  placeholder="R$ 0,00"
                />
              </div>

              <div className="flex flex-col justify-end items-start gap-2">
                <label htmlFor="duration" className="text-sm font-semibold">
                  Duração média
                </label>
                <TimePicker
                  key={`duration-edit-${selectedService?.id}`}
                  granularity="minute"
                  date={parse(
                    serviceDuration.replace(/^(\d{2}):(\d{2})$/, "$1$2"),
                    "HHmm",
                    new Date(),
                  )}
                  onChange={(date) => {
                    if (date) {
                      const formattedTime = format(date, "HH:mm");
                      setServiceDuration(formattedTime);
                    }
                  }}
                />
              </div>

              <div className="grid gap-2">
                <label htmlFor="observation" className="text-sm font-semibold">
                  Observação
                </label>
                <Input
                  id="observation"
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  placeholder="Observação"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="button" onClick={handleSave}>
              Salvar
            </Button>
          </div>
        </Modal>
      )}

      {serviceToActive && (
        <Modal
          isOpen={activeModalOpen}
          onOpenChange={setActiveModalOpen}
          dialogTitle="Ativar serviço"
          dialogDescription={`Tem certeza que deseja ativar o serivo? "${serviceToActive.name}".`}
          className="flex flex-col gap-6 p-6 sm:p-8 max-w-md"
        >
          <h2 className="font-bold">Ativar serviço</h2>

          <div className="grid gap-2">
            <label
              htmlFor="confirm"
              className="text-sm font-medium text-foreground"
            >
              Digite <strong>{serviceToActive.name}</strong> para confirmar:
            </label>
            <Input
              id="confirm"
              value={confirmServiceName}
              onChange={(e) => setConfirmServiceName(e.target.value)}
              placeholder={`Digite: ${serviceToActive.name}`}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setActiveModalOpen(false);
                setConfirmServiceName("");
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              onClick={handleConfirmActive}
              disabled={confirmServiceName !== serviceToActive.name}
            >
              Confirmar ativação
            </Button>
          </div>
        </Modal>
      )}

      {serviceToDelete && (
        <Modal
          isOpen={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          dialogTitle="Excluir serviço"
          dialogDescription={`Ao excluir o serviço "${serviceToDelete.name}", todos os agendamentos vinculados serão desmarcados.`}
          className="flex flex-col gap-6 p-6 sm:p-8 max-w-md"
        >
          <h2 className="font-bold">Deletar serviço</h2>

          <div className="flex items-start gap-3 rounded-lg bg-yellow-100 p-4 text-yellow-800">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <h2 className="text-base font-semibold">Atenção</h2>
              <p className="text-sm">
                Ao confirmar, todos os agendamentos vinculados serão
                desmarcados.
                <br />
                <span className="font-bold text-red-600">
                  Esta ação não poderá ser desfeita.
                </span>
              </p>
            </div>
          </div>

          <div className="grid gap-2">
            <label
              htmlFor="confirm"
              className="text-sm font-medium text-foreground"
            >
              Digite <strong>{serviceToDelete.name}</strong> para confirmar:
            </label>
            <Input
              id="confirm"
              value={confirmServiceName}
              onChange={(e) => setConfirmServiceName(e.target.value)}
              placeholder={`Digite: ${serviceToDelete.name}`}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setConfirmServiceName("");
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={confirmServiceName !== serviceToDelete.name}
            >
              Confirmar exclusão
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default OwnerCompanyServicesTable;
